// chapters/Chapter1/index.js - Nature-themed version
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Play, CheckCircle, Lock, Clock, BookOpen, Leaf, TreePine } from 'lucide-react';
import { Button, Card } from '../../components/ui';

const Chapter1 = ({ chapterId, child, family, onNavigate }) => {
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';
  const currentChapterId = chapterId || '1';

  // Chapter data with nature theme
  const chapterData = {
    title: "Money Mindset & Goal Setting",
    description: "Plant the seeds of smart money habits and watch your financial wisdom grow like a flourishing garden",
    icon: "🌱",
    lessons: [
      {
        id: 1,
        title: "Understanding Money Mindset",
        description: "Like seeds in fertile soil, learn how growth mindset helps money habits flourish",
        icon: "🌰",
        estimatedTime: "10 min",
        natureElement: "Acorn"
      },
      {
        id: 2,
        title: "The Power of SMART Goals",
        description: "Transform money wishes into specific goals, like a gardener plans their garden",
        icon: "🎯",
        estimatedTime: "15 min",
        natureElement: "Compass Rose"
      },
      {
        id: 3,
        title: "The $500 Challenge Breakdown",
        description: "See how small amounts grow into mighty savings, like tiny seeds become mighty oaks",
        icon: "🧮",
        estimatedTime: "12 min",
        natureElement: "Growth Rings"
      },
      {
        id: 4,
        title: "Goal Setting Workshop",
        description: "Cultivate your personal financial garden with intentional planning and care",
        icon: "🏆",
        estimatedTime: "20 min",
        natureElement: "Garden Tools"
      },
      {
        id: 5,
        title: "Mindset Boosters",
        description: "Daily affirmations that nourish your money mindset like sunlight feeds plants",
        icon: "⭐",
        estimatedTime: "8 min",
        natureElement: "Morning Sun"
      }
    ]
  };

  // Load progress from Supabase via API (matches mobile version exactly)
  useEffect(() => {
    if (child?.id) {
      loadCompletions();
    } else {
      console.log('Chapter1: No child provided, showing default UI');
      setLoading(false);
    }
  }, [child?.id]);

  const loadCompletions = async () => {
    try {
      console.log('Chapter1: Loading completions for child:', child?.id);
      
      if (!child?.id) {
        console.log('Chapter1: No child ID provided, skipping progress load');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/lessons/progress/${child.id}`);
      const result = await response.json();
      
      if (result.success) {
        // Filter completions for this specific child (safety measure)
        const childSpecificCompletions = result.data?.filter(completion => {
          const completionChildId = completion.child_id;
          const currentChildId = parseInt(child.id);
          return completionChildId === currentChildId || completionChildId === child.id;
        }) || [];
        
        setCompletions(childSpecificCompletions);
        console.log('Chapter1: Loaded completions:', childSpecificCompletions.length);
      } else {
        console.log('Chapter1: No completions found or API error');
        setCompletions([]);
      }
    } catch (error) {
      console.error('Chapter1: Error loading completions:', error);
      setCompletions([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadCompletions();
    setRefreshing(false);
  };

  // Check if lesson is completed (matches mobile logic)
  const isLessonCompleted = (lessonId) => {
    return completions.some(c => 
      c.chapter_id === parseInt(currentChapterId) && c.lesson_id === lessonId
    );
  };

  // Check if lesson is unlocked (matches mobile logic)
  const isLessonUnlocked = (lessonId) => {
    const chapterInt = parseInt(currentChapterId);
    
    // Chapter 1, Lesson 1 always unlocked
    if (chapterInt === 1 && lessonId === 1) return true;
    
    // Check previous lesson in same chapter
    if (lessonId > 1) {
      return isLessonCompleted(lessonId - 1);
    }
    
    // First lesson of new chapter - check last lesson of previous chapter
    return completions.some(c => 
      c.chapter_id === chapterInt - 1 && c.lesson_id === 5
    );
  };

  // Get lesson status for UI display
  const getLessonStatus = (lessonId) => {
    if (isLessonCompleted(lessonId)) return 'completed';
    if (isLessonUnlocked(lessonId)) return 'unlocked';
    return 'locked';
  };

  // Calculate chapter progress
  const calculateProgress = () => {
    const completedLessons = chapterData.lessons.filter(lesson => 
      isLessonCompleted(lesson.id)
    ).length;
    return Math.round((completedLessons / chapterData.lessons.length) * 100);
  };

  // Handle lesson navigation
  const handleLessonPress = (lesson) => {
    const status = getLessonStatus(lesson.id);
    
    if (status === 'locked') {
      // Could show a toast or modal here explaining why it's locked
      console.log('Lesson is locked');
      return;
    }
    
    // Navigate to lesson
    onNavigate('financialLiteracyLesson', {
      chapterId: currentChapterId,
      lessonId: lesson.id
    });
  };

  // Get status icon with nature theme
  const getStatusIcon = (lessonId) => {
    const status = getLessonStatus(lessonId);
    
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-emerald-600" />;
      case 'unlocked':
        return <Leaf className="w-6 h-6 text-amber-600" />;
      case 'locked':
      default:
        return <Lock className="w-6 h-6 text-stone-400" />;
    }
  };

  // Get status styling with nature colors
  const getStatusStyling = (lessonId) => {
    const status = getLessonStatus(lessonId);
    
    switch (status) {
      case 'completed':
        return 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100 shadow-md';
      case 'unlocked':
        return 'border-amber-300 bg-amber-50 hover:bg-amber-100 cursor-pointer shadow-sm hover:shadow-md transition-all duration-300';
      case 'locked':
      default:
        return 'border-stone-300 bg-stone-100 opacity-60 cursor-not-allowed';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-amber-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="mt-4 text-stone-600">Nurturing your learning garden...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-amber-50 p-6">
      <div className="max-w-4xl mx-auto">
        
        {/* Header with nature elements */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => onNavigate('financialLiteracyProgram')}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-800 hover:bg-white/80 rounded-xl px-4 py-2"
          >
            <ArrowLeft className="w-5 h-5" />
            <TreePine className="w-4 h-4" />
            Back to Forest
          </Button>
          
          <Button
            variant="outline"
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 bg-white/80 border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl"
          >
            {refreshing ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
            ) : (
              <Leaf className="w-4 h-4" />
            )}
            {refreshing ? 'Growing...' : 'Refresh Garden'}
          </Button>
        </div>

        {/* Chapter Title & Progress with organic styling */}
        <div className="text-center mb-12">
          <div className="relative mb-6">
            <div className="text-7xl mb-4 filter drop-shadow-md">{chapterData.icon}</div>
            <div className="absolute -top-2 -right-8 text-2xl opacity-60">🍃</div>
            <div className="absolute -bottom-2 -left-6 text-xl opacity-40">🌿</div>
          </div>
          
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-700 via-green-600 to-amber-600 bg-clip-text text-transparent mb-4">
            Chapter {currentChapterId}: {chapterData.title}
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            {chapterData.description}
          </p>
          
          {/* Organic Progress Bar */}
          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-sm text-stone-600 mb-3">
              <span className="flex items-center gap-1">
                <Leaf className="w-4 h-4" />
                Growth Progress
              </span>
              <span className="font-medium">{calculateProgress()}% Flourishing</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-4 shadow-inner">
              <div
                className="bg-gradient-to-r from-emerald-500 via-green-500 to-amber-500 h-4 rounded-full transition-all duration-700 shadow-sm"
                style={{ width: `${calculateProgress()}%` }}
              ></div>
            </div>
            <div className="mt-2 text-xs text-stone-500 italic">
              Like rings in a tree, each lesson builds upon the last
            </div>
          </div>
        </div>

        {/* Lessons with organic cards */}
        <div className="space-y-6">
          {chapterData.lessons.map((lesson, index) => {
            const status = getLessonStatus(lesson.id);
            
            return (
              <Card
                key={lesson.id}
                className={`p-6 transition-all duration-300 ${getStatusStyling(lesson.id)} border-2 rounded-2xl relative overflow-hidden`}
                onClick={() => handleLessonPress(lesson)}
              >
                {/* Subtle nature pattern overlay */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
                  <div className="text-6xl transform rotate-12 text-current">🌿</div>
                </div>
                
                <div className="flex items-start gap-6 relative z-10">
                  
                  {/* Lesson Number & Icon with organic styling */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className={`w-14 h-14 rounded-full border-3 flex items-center justify-center text-xl font-bold mb-3 shadow-md ${
                        status === 'completed' ? 'bg-emerald-100 border-emerald-400 text-emerald-700' :
                        status === 'unlocked' ? 'bg-amber-100 border-amber-400 text-amber-700' :
                        'bg-stone-100 border-stone-300 text-stone-500'
                      }`}>
                        {lesson.id}
                      </div>
                      {status === 'completed' && (
                        <div className="absolute -top-1 -right-1 text-sm">✨</div>
                      )}
                    </div>
                    <div className="text-3xl text-center filter drop-shadow-sm">
                      {lesson.icon}
                    </div>
                  </div>

                  {/* Lesson Content with nature-inspired text */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-semibold text-stone-800 mb-2">
                          {lesson.title}
                        </h3>
                        <p className="text-stone-600 mb-4 leading-relaxed">
                          {lesson.description}
                        </p>
                        <div className="flex items-center gap-6 text-sm text-stone-500">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {lesson.estimatedTime}
                          </div>
                          <div className="flex items-center gap-2">
                            <Leaf className="w-4 h-4" />
                            {lesson.natureElement}
                          </div>
                          {status === 'completed' && (
                            <div className="text-emerald-600 font-medium flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Bloomed
                            </div>
                          )}
                          {status === 'locked' && (
                            <div className="text-stone-400 flex items-center gap-1">
                              <Lock className="w-4 h-4" />
                              Awaiting previous growth
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {getStatusIcon(lesson.id)}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Chapter Summary with nature theme */}
        <div className="mt-16 text-center">
          <Card className="p-8 bg-gradient-to-br from-emerald-600 via-green-600 to-amber-600 text-white rounded-3xl shadow-xl relative overflow-hidden">
            {/* Nature background elements */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <div className="absolute top-4 left-8 text-4xl">🌿</div>
              <div className="absolute top-12 right-12 text-3xl">🍃</div>
              <div className="absolute bottom-8 left-16 text-2xl">🌱</div>
              <div className="absolute bottom-4 right-8 text-3xl">🌸</div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4">
                Ready to Cultivate Your Money Garden?
              </h2>
              <p className="text-lg opacity-90 mb-6 leading-relaxed">
                Like a master gardener, complete all lessons to help your financial wisdom bloom and unlock the next chapter of growth!
              </p>
              <div className="text-4xl mb-6">🌳</div>
              {calculateProgress() === 100 ? (
                <div className="bg-white/20 rounded-2xl p-6 backdrop-blur-sm">
                  <p className="text-2xl font-semibold mb-2">
                    🎉 Garden Complete!
                  </p>
                  <p className="text-lg opacity-90">
                    Your financial foundation has taken root. Ready to explore new terrain in Chapter 2?
                  </p>
                </div>
              ) : (
                <div className="bg-white/20 rounded-2xl p-6 backdrop-blur-sm">
                  <p className="text-lg mb-2">
                    <span className="font-semibold">
                      {5 - chapterData.lessons.filter(l => isLessonCompleted(l.id)).length} lessons
                    </span> need nurturing to complete your garden
                  </p>
                  <p className="text-sm opacity-80">
                    Each lesson you complete helps your financial wisdom grow stronger
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Chapter1;